import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { range, intersection } from "ramda";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScrollIntoViewIfNeeded from "~/components/ScrollIntoViewIfNeeded";
import ListItemButton from "@mui/material/ListItemButton";

const getListItemStyles = depth => ({
  paddingLeft: depth * 16
});

function OutlineNode({
  items,
  title,
  page,
  onNodeClick,
  depth,
  to,
  pages,
  offset,
  onSidebarClose,
  isGreaterThanMedium,
  closeOnClick,
  openDefault
}) {
  const [isOpen, setIsOpen] = useState(openDefault);
  const isSelected = intersection(range(page + offset, to + offset + 1), pages).length > 0;

  const Icon = isOpen ? ExpandLessIcon : ExpandMoreIcon;

  function onIconClick(event) {
    event.stopPropagation();
    setIsOpen(!isOpen);
  }

  function onItemClick() {
    onNodeClick(page + offset);
    setIsOpen(true);
  }

  return (
    <ScrollIntoViewIfNeeded active={isSelected}>
      <>
        <ListItemButton onClick={onItemClick} selected={isSelected} style={getListItemStyles(depth)}>
          <span
            dangerouslySetInnerHTML={{ __html: title }}
            style={{
              fontSize: "1.42rem",
              lineHeight: 1.5,
              margin: "4px 0"
            }}
          />
          {items.length > 0 && <Icon onClick={onIconClick} />}
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <OutlineList
            onNodeClick={onNodeClick}
            outline={items}
            pages={pages}
            offset={offset}
            onSidebarClose={onSidebarClose}
            isGreaterThanMedium={isGreaterThanMedium}
            closeOnClick={closeOnClick}
            openDefault={openDefault}
          />
        </Collapse>
      </>
    </ScrollIntoViewIfNeeded>
  );
}

OutlineNode.defaultProps = {
  items: [],
  title: "",
  page: 1,
  depth: 1,
  to: 1,
  pages: [],
  offset: 0,
  openDefault: true
};

OutlineNode.propTypes = {
  items: PropTypes.array,
  title: PropTypes.string,
  page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onNodeClick: PropTypes.func.isRequired,
  depth: PropTypes.number,
  to: PropTypes.number,
  pages: PropTypes.array,
  offset: PropTypes.number,
  onSidebarClose: PropTypes.func,
  isGreaterThanMedium: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  openDefault: PropTypes.bool
};

function OutlineList({
  onNodeClick,
  outline,
  pages,
  className,
  isGreaterThanMedium,
  onSidebarClose,
  offset,
  closeOnClick,
  openDefault
}) {
  function onClick(page) {
    onNodeClick(page);

    if (!isGreaterThanMedium || closeOnClick) {
      onSidebarClose();
    }
  }

  return (
    <List className={className} disablePadding>
      {outline.map(node => (
        <OutlineNode
          key={`${node.depth}_${node.title}`}
          onNodeClick={onClick}
          pages={pages}
          offset={offset}
          onSidebarClose={onSidebarClose}
          isGreaterThanMedium={isGreaterThanMedium}
          closeOnClick={closeOnClick}
          openDefault={openDefault}
          {...node}
        />
      ))}
    </List>
  );
}

OutlineList.defaultProps = {
  outline: [],
  pages: [],
  className: "",
  offset: 0,
  closeOnClick: false,
  openDefault: true
};

OutlineList.propTypes = {
  onNodeClick: PropTypes.func.isRequired,
  outline: PropTypes.array,
  pages: PropTypes.array,
  className: PropTypes.string,
  isGreaterThanMedium: PropTypes.bool,
  onSidebarClose: PropTypes.func,
  offset: PropTypes.number,
  closeOnClick: PropTypes.bool,
  openDefault: PropTypes.bool
};

export default OutlineList;
